import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";

// Office location data structured for better organization
const officeLocations = [
  {
    id: 1,
    name: "Dev Bhoomi TMT Sales Pvt. Ltd.",
    url: "https://devbhoomitmt.com/",
    address:
      "Dev Bhoomi Group Companies, Admin Block at The Gulmohar Grand, 8 mile stone, Una Chandigarh Highway Behdala, Una, Himachal Pradesh 174306",
  },
  {
    id: 2,
    name: "Dev Bhoomi Motors Pvt. Ltd.",
    url: "https://dealers.skoda-auto.co.in/dev-bhoomi-skoda-showroom-and-service-dev-bhoomi-motors-pvt-ltd-car-dealer-gutkar-mandi-67723/Home?utm_source=locator&utm_medium=googleplaces",
    address:
      "Ground Floor, NH 21, Opposite Reliance Petrol Pump, Mandi (Gutkar), Himachal Pradesh 175021",
  },
  {
    id: 3,
    name: "Dev Bhoomi Cars Pvt. Ltd.",
    url: "https://devbhoomi.hyundaimotor.in",
    address:
      "Ground Floor, NH 21, Opposite Reliance Petrol Pump, Mandi (Gutkar), Himachal Pradesh 175021",
  },
  {
    id: 4,
    name: "Dev Bhoomi Wheels LLP",
    url: "https://devbhoomi.hyundaimotor.in",
    address:
      "Plot No 104, Industrial Area, Shimla (Shoghi), Himachal Pradesh 171001",
  },
  {
    id: 5,
    name: "Gulmohar Luxe Hotels Pvt. Ltd.",
    url: "https://thegulmohargrand.com",
    address:
      "Dev Bhoomi Group Companies, Admin Block at The Gulmohar Grand, 8 mile stone, Una Chandigarh Highway Behdala, Una, Himachal Pradesh 174306",
  },
  {
    id: 6,
    name: "Grand Cinemaz Pvt. Ltd.",
    url: 'https://grandcinemaz.com',
    address: "Dev Bhoomi Group Companies, Admin Block at The Gulmohar Grand, 8 mile stone, Una Chandigarh Highway Behdala, Una, Himachal Pradesh 174306"
  },
  {
    id: 7,
    name: "FDL Solar Power Pvt. Ltd.",
    url: "https://fdlsolarpower.com/",
    address:
      "Dev Bhoomi Group Companies, Admin Block at The Gulmohar Grand, 8 mile stone, Una Chandigarh Highway Behdala, Una, Himachal Pradesh 174306",
  },
  {
    id: 8,
    name: "Dev Bhoomi Luxestate",
    url: "https://devbhoomiluxestate.com/",
    address:
      "Dev Bhoomi Group Companies, Admin Block at The Gulmohar Grand, 8 mile stone, Una Chandigarh Highway Behdala, Una, Himachal Pradesh 174306",
  },
  {
    id: 9,
    name: "Dev Bhoomi Electrical Car Pvt. Ltd.",
    address:
      "Dev Bhoomi Group Companies,Admin Block at The Gulmohar Grand, 8 mile stone, Una Chandigarh Highway Behdala, Una, Himachal Pradesh 174306",
  },
];

const OurCompanies = () => {
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [isHovering, setIsHovering] = useState(null);

  // Animation variants for card container
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
      },
    },
  };

  // Animation variants for individual cards
  const cardVariants = {
    hidden: { y: 60, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 80,
        damping: 14,
      },
    },
    hover: {
      y: -8,
      boxShadow:
        "0 25px 40px -15px rgba(0, 0, 0, 0.15), 0 15px 20px -5px rgba(0, 0, 0, 0.06)",
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 15,
      },
    },
  };

  // Animation variants for title
  const titleVariants = {
    hidden: { opacity: 0, y: -30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.3,
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  // Subtle floating animation for card elements
  const floatAnimation = {
    y: [0, -10, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      repeatType: "reverse",
      ease: "easeInOut",
    },
  };

  // Section background with parallax effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const parallaxElements = document.querySelectorAll(".parallax-bg");

      parallaxElements.forEach((element) => {
        element.style.transform = `translateY(${scrollPosition * 0.05}px)`;
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden bg-gray-50 dark:bg-slate-900">
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-gradient-to-br from-gold-100 to-gold-300 dark:from-amber-600 dark:to-orange-500 opacity-20 blur-3xl parallax-bg"></div>
        <div className="absolute -bottom-60 -left-60 w-96 h-96 rounded-full bg-gradient-to-br from-amber-100 to-amber-300 dark:from-orange-500 dark:to-red-600 opacity-20 blur-3xl parallax-bg"></div>
      </div>

      {/* Content container */}
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Title section with animation */}
        <motion.div
          className="text-center mb-20"
          initial="hidden"
          animate="visible"
          variants={titleVariants}
        >
          <div className="flex justify-center items-center mb-3">
            <div className="h-px w-12 bg-gradient-to-r from-amber-300 to-amber-500 dark:from-orange-400 dark:to-amber-600"></div>
            <span className="mx-4 text-amber-600 dark:text-orange-400 font-medium tracking-widest text-sm uppercase">
              Exclusive Establishments
            </span>
            <div className="h-px w-12 bg-gradient-to-r from-amber-500 to-amber-300 dark:from-amber-600 dark:to-orange-400"></div>
          </div>

          <h2 className="text-5xl md:text-6xl font-serif font-light mb-6 text-gray-900 dark:text-slate-100">
            Our <span className="font-medium italic">Distinguished</span>{" "}
            Locations
          </h2>

          <p className="mt-6 text-xl text-gray-600 dark:text-slate-300 max-w-3xl mx-auto font-light leading-relaxed">
            Experience the exceptional hospitality and premium services across
            our collection of prestigious establishments throughout Himachal
            Pradesh
          </p>

          <motion.div
            className="h-0.5 w-24 bg-gradient-to-r from-amber-300 to-amber-500 dark:from-orange-400 dark:to-amber-600 mx-auto rounded-full mt-8"
            animate={{ width: [0, 96] }}
            transition={{ delay: 0.6, duration: 1.2, ease: "easeOut" }}
          ></motion.div>
        </motion.div>

        {/* Cards grid with staggered animation */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {officeLocations.map((office) => (
            <motion.a
              href={office.url}
              target="_blank"
              rel="noopener noreferrer"
              key={office.id}
              className={`bg-white dark:bg-slate-800 rounded-lg overflow-hidden group transition-all duration-300 ${
                selectedOffice === office.id
                  ? "ring-2 ring-amber-400 dark:ring-orange-500"
                  : ""
              }`}
              variants={cardVariants}
              whileHover="hover"
              onMouseEnter={() => setIsHovering(office.id)}
              onMouseLeave={() => setIsHovering(null)}
              onClick={() => setSelectedOffice(office.id)}
              style={{
                boxShadow:
                  "0 10px 30px -5px rgba(0, 0, 0, 0.08), 0 5px 15px -5px rgba(0, 0, 0, 0.05)",
              }}
            >
              {/* Card Header */}
              <div className="relative h-3 bg-gradient-to-r from-amber-400 via-amber-300 to-amber-500 dark:from-orange-400 dark:via-amber-500 dark:to-orange-500 overflow-hidden">
                <motion.div
                  className="absolute inset-0 w-1/2 h-full bg-white dark:bg-orange-100 opacity-30 transform -skew-x-12"
                  animate={{
                    x: isHovering === office.id ? ["150%", "-150%"] : "150%",
                  }}
                  transition={{
                    duration: 1.2,
                    ease: "easeInOut",
                    delay: 0.2,
                  }}
                />
              </div>

              {/* Card Content */}
              <div className="p-8">
                <div className="flex items-center mb-6">
                  <motion.div
                    className="h-12 w-12 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 dark:from-orange-400 dark:to-amber-600 flex items-center justify-center text-white font-serif text-lg shadow-lg"
                    animate={isHovering === office.id ? floatAnimation : {}}
                  >
                    {office.id}
                  </motion.div>
                  <h3 className="ml-4 text-xl font-medium text-gray-900 dark:text-slate-100 group-hover:text-amber-700 dark:group-hover:text-orange-300 transition-colors duration-300">
                    {office.name}
                  </h3>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-amber-500 dark:text-orange-400">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M11.54 22.351l.07.04.028.016a.76.76 0 00.723 0l.028-.015.071-.041a16.975 16.975 0 001.144-.742 19.58 19.58 0 002.683-2.282c1.944-1.99 3.963-4.98 3.963-8.827a8.25 8.25 0 00-16.5 0c0 3.846 2.02 6.837 3.963 8.827a19.58 19.58 0 002.682 2.282 16.975 16.975 0 001.145.742zM12 13.5a3 3 0 100-6 3 3 0 000 6z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <p className="ml-3 text-gray-600 dark:text-slate-300 font-light">
                    {office.address}
                  </p>
                </div>
              </div>
            </motion.a>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default OurCompanies;
